import { Emergency, PaginatedResponse } from '../types'
import useSWR, { mutate } from 'swr'
import { fetcher, apiRequest } from '../utils/api'
import { useState } from 'react'

function EmergencyManager() {
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize] = useState(20)
  
  const { data: emergenciesResponse, error: emergenciesError } = useSWR<PaginatedResponse<Emergency>>(
    `/emergencies?page=${currentPage}&limit=${pageSize}`, 
    fetcher,
    {
      refreshInterval: 5000,
    }
  )
  
  const { data: activeEmergenciesResponse } = useSWR<PaginatedResponse<Emergency>>(
    `/emergencies?status=active&page=1&limit=100`, 
    fetcher,
    {
      refreshInterval: 5000,
    }
  )
  
  const emergencies = emergenciesResponse?.data || []
  const activeEmergencies = activeEmergenciesResponse?.data || []
  const pagination = emergenciesResponse?.pagination

  const handleResolve = async (emergencyId: string) => {
    if (!confirm('Are you sure you want to mark this emergency as resolved?')) return

    try {
      await apiRequest(`/emergencies/${emergencyId}/resolve`, {
        method: 'PATCH',
      })

      mutate(`/emergencies?page=${currentPage}&limit=${pageSize}`)
      mutate(`/emergencies?status=active&page=1&limit=100`)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to resolve emergency')
    }
  }

  if (emergenciesError) {
    console.error('Emergencies error:', emergenciesError)
    return (
      <div className="alert alert-error">
        Failed to load emergencies: {emergenciesError?.message || 'Unknown error'}
      </div>
    )
  }

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage)
  }

  return (
    <div>
      <h2>Emergency Management</h2>
      
      {activeEmergencies.length > 0 && (
        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ color: '#dc3545' }}>🚨 Active Emergencies ({activeEmergencies.length})</h3>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Hospital</th>
                <th>Room</th>
                <th>Floor</th>
                <th>Triggered</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {activeEmergencies.map((emergency) => (
                <tr key={emergency.id} style={{ backgroundColor: '#fff5f5' }}>
                  <td>{emergency.hospital_name}</td>
                  <td>{emergency.room_label}</td>
                  <td>{emergency.room_floor}</td>
                  <td>{new Date(emergency.triggered_at).toLocaleString()}</td>
                  <td>
                    <span className="status-active">ACTIVE</span>
                  </td>
                  <td>
                    <button 
                      className="btn btn-success"
                      onClick={() => handleResolve(emergency.id)}
                    >
                      Mark Resolved
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeEmergencies.length === 0 && (
        <div className="alert alert-success" style={{ marginBottom: '2rem' }}>
          ✅ No active emergencies
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h3>Recent Emergencies</h3>
        {pagination && (
          <div style={{ fontSize: '0.9rem', color: 'var(--text-muted)' }}>
            Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} emergencies
          </div>
        )}
      </div>
      
      <table className="admin-table">
        <thead>
          <tr>
            <th>Hospital</th>
            <th>Room</th>
            <th>Floor</th>
            <th>Triggered</th>
            <th>Resolved</th>
            <th>Status</th>
            <th>Duration</th>
          </tr>
        </thead>
        <tbody>
          {emergencies.map((emergency) => (
            <tr key={emergency.id}>
              <td>{emergency.hospital_name}</td>
              <td>{emergency.room_label}</td>
              <td>{emergency.room_floor}</td>
              <td>{new Date(emergency.triggered_at).toLocaleString()}</td>
              <td>
                {emergency.resolved_at 
                  ? new Date(emergency.resolved_at).toLocaleString()
                  : '-'
                }
              </td>
              <td>
                <span className={`status-${emergency.status}`}>
                  {emergency.status.toUpperCase()}
                </span>
              </td>
              <td>
                {emergency.resolved_at 
                  ? Math.round((new Date(emergency.resolved_at).getTime() - new Date(emergency.triggered_at).getTime()) / 1000 / 60) + ' min'
                  : 'Ongoing'
                }
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {emergencies.length === 0 && (
        <p>No emergencies recorded yet.</p>
      )}
      
      {pagination && pagination.totalPages > 1 && (
        <div className="pagination-controls">
          <button 
            className="btn btn-secondary" 
            onClick={() => handlePageChange(1)}
            disabled={pagination.page === 1}
          >
            First
          </button>
          <button 
            className="btn btn-secondary" 
            onClick={() => handlePageChange(pagination.page - 1)}
            disabled={!pagination.hasPrevPage}
          >
            Previous
          </button>
          <span className="pagination-info">
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <button 
            className="btn btn-secondary" 
            onClick={() => handlePageChange(pagination.page + 1)}
            disabled={!pagination.hasNextPage}
          >
            Next
          </button>
          <button 
            className="btn btn-secondary" 
            onClick={() => handlePageChange(pagination.totalPages)}
            disabled={pagination.page === pagination.totalPages}
          >
            Last
          </button>
        </div>
      )}
    </div>
  )
}

export default EmergencyManager